import { ReactElement } from 'react';
import { Group, MultiSelect, Stack, Text, UnstyledButton } from '@mantine/core';
import { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { PractitionerMultiSelect } from './PractitionerMultiSelect';
import { useSelectedPractitionerIds } from '../hooks/useSelectedPractitionerIds';
import { mapPractitionerIdsToProviderRefs } from '../utils/common';

interface AvailabilityListFiltersProps {
  roles: { value: string; label: string }[];
  locations: { value: string; label: string }[];
  selectedRole?: string[];
  selectedProvider?: string[];
  selectedLocation?: string[];
  onRoleChange?: (roles: string[]) => void;
  onProviderChange?: (providers: string[]) => void;
  onLocationChange?: (locations: string[]) => void;
  onClearFilters?: () => void;
  practitioners?: Practitioner[];
  practitionerRoles?: PractitionerRole[];
}

export function AvailabilityListFilters({
  roles,
  locations,
  selectedRole = [],
  selectedProvider = [],
  selectedLocation = [],
  onRoleChange,
  onProviderChange,
  onLocationChange,
  onClearFilters,
  practitioners = [],
  practitionerRoles = [],
}: AvailabilityListFiltersProps): ReactElement {
  const hasActiveFilters = selectedRole.length > 0 || selectedProvider.length > 0 || selectedLocation.length > 0;

  const selectedPractitionerIds = useSelectedPractitionerIds(selectedProvider, practitioners, practitionerRoles);

  const handleOnChangeIds = (ids: string[]) => {
    if (!onProviderChange) return;

    const newProviderRefs = mapPractitionerIdsToProviderRefs(ids, practitioners, practitionerRoles);
    onProviderChange(newProviderRefs);
  };

  return (
    <Stack w="100%" p={16} bg="white" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }} gap="md">
      <Group justify="space-between" align="center">
        <Text fw={600} size="md">
          Filters
        </Text>
        {hasActiveFilters && (
          <UnstyledButton
            p="4px 8px"
            style={{
              borderRadius: '4px',
              transition: 'background-color 0.2s ease-in-out',
              textDecoration: 'none',
            }}
            onClick={onClearFilters}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <Text size="sm" c="blue">
              Remove all filters
            </Text>
          </UnstyledButton>
        )}
      </Group>

      <Stack gap="md">
        <MultiSelect
          label="Role"
          placeholder="All roles"
          data={roles}
          value={selectedRole}
          onChange={onRoleChange}
          clearable
          searchable
        />

        <PractitionerMultiSelect
          valueIds={selectedPractitionerIds}
          onChangeIds={handleOnChangeIds}
          practitioners={practitioners}
          label="Provider"
        />

        <MultiSelect
          label="Locations"
          placeholder="All locations"
          data={locations}
          value={selectedLocation}
          onChange={onLocationChange}
          clearable
          searchable
        />
      </Stack>
    </Stack>
  );
}
